import {
  ActionIcon,
  Box,
  Center,
  CloseButton,
  Group,
  Skeleton,
  Title, UnstyledButton
} from "@mantine/core";
import { useFullscreen } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconArrowsMaximize, IconRectangle
} from "@tabler/icons-react";
import BottomTray from "core/components/approval/BottomTray";
import SingleLayout from "core/components/approval/SingleLayout";
import { CaptureEventType } from "core/components/capture/CaptureThumb";
import { useCallback, useEffect, useRef, useState } from "react";
import { AppConstants, ApprovalImageToolbarModes, ApprovalLayoutTypes } from "utils/constants";
import { logger } from "utils/logger";

import classes from "./StyleAssetViewer.module.scss";
import { PrimaryBadge } from "core/components/badge/PrimaryBadge";
import Toolbar from "core/components/approval/Toolbar";
import { ApprovalEventTypes } from "core/components/approval/AssetViewer";
import { useSearchParams } from "react-router-dom";
import StyleAssetInfoPanel from "./StyleAssetInfoPanel";
// import AssetInfoPanel from "modules/Distributions/features/style/AssetInfoPanel";

const StyleAssetViewer = ({ style, index, onClose, user }) => {
  const [comparisonItems, setComparisonItems] = useState([style?.assets?.[index]]);
  const [imageControlSettings, setImageControlSettings] = useState({
    zoomDirection: 1,
  });
  const [selectedCompareFrame, setSelectedCompareFrame] = useState(0);
  const [selectedItem, setSelectedItem] = useState(style?.assets?.[index] ?? {
    ...style?.assets?.[index],
    id: style?.assets?.[index]?.assignedSequence,
  });
  const [selectedItems, setSelectedItems] = useState([style?.assets?.[index]]);
  const imageLocHash = useRef({});
  const { toggle } = useFullscreen();
  const [searchParams, setSearchParams] = useSearchParams();
  const onCaptureChange = () => {};

  // Custom localStorage hook implementation
  const getStoredSettings = () => {
    try {
      const stored = localStorage.getItem("op-distribution-display-settings");
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return {
          layoutType: ApprovalLayoutTypes.SINGLE,
          infoOpen: false,
          filterOpen: false,
          thumbnailSize: 150,
          isCommenting: false,
          sortBy: "actionSort",
          sortDirection: "asc",
          syncZoomPan: true,
          bottomTrayOpen: true,
          ...parsed,
        };
      }
    } catch (error) {
      logger.log("Error reading localStorage:", error);
    }
    // Return defaults if no stored value or error
    return {
      layoutType: ApprovalLayoutTypes.SINGLE,
      infoOpen: false,
      filterOpen: false,
      thumbnailSize: 150,
      isCommenting: false,
      sortBy: "actionSort",
      sortDirection: "asc",
      syncZoomPan: true,
      bottomTrayOpen: true,
    };
  };

  const [approvalSettings, setApprovalSettingsState] =
    useState(getStoredSettings);

  const setApprovalSettings = useCallback(
    (newSettings) => {
      const updatedSettings =
        typeof newSettings === "function"
          ? newSettings(approvalSettings)
          : newSettings;

      try {
        localStorage.setItem(
          "op-distribution-display-settings",
          JSON.stringify(updatedSettings)
        );
        setApprovalSettingsState(updatedSettings);
        logger.log("Settings saved to localStorage:", updatedSettings);
      } catch (error) {
        logger.log("Error saving to localStorage:", error);
        setApprovalSettingsState(updatedSettings);
      }
    },
    [approvalSettings]
  );

  // Debug logging to see what's happening with localStorage
  useEffect(() => {
    logger.log("AssetViewer mounted, approvalSettings:", approvalSettings);
    logger.log(
      "localStorage value:",
      localStorage.getItem("op-distribution-display-settings")
    );
  }, []);

  useEffect(() => {
    logger.log("approvalSettings changed:", approvalSettings);
  }, [approvalSettings]);
  const toggleRef = useRef();

  useEffect(() => {
    // Ensure the referenced div exists
    const toggleDiv = toggleRef.current;
    const controlDiv = document.getElementById("openSeaDragon-controller");

    if (controlDiv && toggleDiv) {
      const firstChild = controlDiv.firstElementChild;

      if (firstChild) {
        firstChild.appendChild(toggleDiv);
      }
    }
  }, []);

  const onImageEvent = (type, data, originalEvent) => {
    var cItems, items;

    switch (type) {
      case CaptureEventType.IMAGE_CLICK: {
        logger.log(comparisonItems);

        items = [data];

        // setSelectedItems([data]);

        // if no comparison items is selected
        if (comparisonItems && comparisonItems.length) {
          cItems = [...comparisonItems];
          if (comparisonItems.length === 1) {
            cItems[1] = data;
          } else {
            cItems[selectedCompareFrame] = data;
          }
        } else {
          cItems = [data];
        }

        setSelectedItem({ ...data });

        // searchParams.set('captureId',data.captureId);
        // setSearchParams(searchParams);

        setSelectedItems(items);
        setComparisonItems(cItems);

        // if(comparisonItems && comparisonItems.length){
        //   var items = [...comparisonItems];

        //   items[selectedCompareFrame] = data;
        //   setComparisonItems(items)
        // }else{
        //   setComparisonItems([data])
        // }

        break;
      }

      default: {
        break;
      }
    }
  };

  // const onPanelToggleClick = () => {
  //   setPanelOpen(!panelOpen);
  // };

  const settingsChange = (type, data) => {
    logger.log(type, data);
    switch (type) {
      case ApprovalEventTypes.SETTINGS_CHANGE: {
        setApprovalSettings((prevSettings) => ({ ...prevSettings, ...data }));
        break;
      }
      default: {
        break;
      }
    }

    let urlParms = Object.fromEntries([...searchParams]);
    // const urlParms = new URLSearchParams(window.location.search);
    logger.log(urlParms, " PARMS CHANGE");
    if (data.layoutType !== urlParms.layoutType) {
      // set the settings as part of the url.
      searchParams.set("layoutType", data.layoutType);

      urlParms.layoutType = data.layoutType;

      setSearchParams(urlParms);
    }
  };



  /**
   * Handler for image click
   */
  const onImageControlClick = useCallback(
    (type) => {
      logger.log("IMAGEW ZOOM CLICK");
      switch (type) {
        case ApprovalImageToolbarModes.ZOOM_RESET:
        case ApprovalImageToolbarModes.ZOOM_OUT:
        case ApprovalImageToolbarModes.ZOOM_IN: {
          let props = { ...imageControlSettings, zoomDirection: type };
          setImageControlSettings(props);
          break;
        }
        default: {
          break;
        }
      }
    },
    [imageControlSettings]
  );
  // Hotkey handler for zoom controls
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle hotkeys when not in an input field
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      switch (event.key) {
        case "+":
        case "=": // Handle both + and = keys (same key on many keyboards)
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_IN);
          break;
        case "-":
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_OUT);
          break;
        case "0":
          event.preventDefault();
          onImageControlClick(ApprovalImageToolbarModes.ZOOM_RESET);
          break;
        default:
          break;
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onImageControlClick]);

logger.log(selectedItem, "SELECTED ITEM");
  return (
    <Box
      px="lg"
      pt="lg"
      className={`${classes.panel} ${
        approvalSettings.infoOpen ? classes["info-open"] : ""
      } ${approvalSettings.filterOpen ? classes["filter-open"] : ""}`}
    >
      <Group
        justify="space-between"
        align="center"
        pb="lg"
        px={"lg"}
        className={classes.header}
      >
        <Group justify="space-between">
          <UnstyledButton
            onClick={() => {
              onClose();
            }}
          >
            <Group>
              <IconArrowLeft
                size={25}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
              <Title order={5} className="unhightable">
                {style.name}
              </Title>
            </Group>
          </UnstyledButton>

          {/* <Title className="main-content-heading" order={5}>
            {style.name}
          </Title> */}
          <PrimaryBadge>{style.styleNumber}</PrimaryBadge>
        </Group>
        <Group>
          <ActionIcon onClick={toggle} variant="default">
            <IconArrowsMaximize
              size={AppConstants.ICON_SIZE}
              stroke={AppConstants.ICON_STROKE_WIDTH}
            />
          </ActionIcon>
          <CloseButton onClick={onClose} />
        </Group>
      </Group>
      {selectedItem && selectedItem.preview ? (
        <Group
          wrap="nowrap"
          align="flex-start"
          style={{ height: "100%", flexGrow: 1 }}
          gap={0}
          className={classes.seaDragon}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "calc(100vh - 72px)",
              gap: 0,
            }}
          >
            <Box
              className={"scan-image-holder"}
              style={{
                cursor: "grab",
                height: "100%",
                minHeight: 300,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
              }}

              // onClick={() => setImageModalOpen(true)}
            >
              <Toolbar
                settingsChange={(type, data) => settingsChange(type, data)}
                settings={approvalSettings}
                selectedItems={selectedItems}
                onImageControlClick={onImageControlClick}
                selectedCompareFrame={selectedCompareFrame}
                onCaptureChange={onCaptureChange}
                showFilterToggle={false}
                layoutOptions={[{
                  value: ApprovalLayoutTypes.SINGLE,
                  label: <Center><IconRectangle size={16} /></Center>
                }]}
              />

              {/* <ResolverImage fit='contain' src={scan.url} width={'100%'} height={300} /> */}
              {selectedItem ? (
                <SingleLayout
                  image={selectedItem}
                  showSettings={false}
                  showToolbar={true}
                  settings={{ layoutType: "" }}
                  instanceId="styleAssetViewer"
                />
              ) : null}
            </Box>

            <BottomTray
              captures={style?.assets?.map((val) => ({
                ...val,
                previewMid: val.preview,
                previewHi: val.preview,
                previewLo: val.thumb,
                id: val.assignedSequence,
              })) ?? []}
              onImageEvent={onImageEvent}
              selectedItems={[selectedItem]}
              comparisonItems={comparisonItems}
              selectedCompareFrame={null}
              imageLocHash={imageLocHash}
              onCaptureChange={onCaptureChange}
              showComapirsonSelection={false}
              isSearching={false}
              searchCursor={null}
              loadNextPage={null}
              showLoading={false}
              bottomTrayOpen={approvalSettings.bottomTrayOpen}
              onBottomTrayOpenChange={(val) => {
                setApprovalSettings((prevSettings) => ({ ...prevSettings, bottomTrayOpen: val }));
              }}
            />
          </div>
          <div
            style={{ width: "100%", flexGrow: 1, alignSelf: "stretch" }}
            className={classes.infoPanel}
          >
            <StyleAssetInfoPanel selectedItem={selectedItem} style={style} user={user} />
          </div>
        </Group>
      ) : (
        <Skeleton width={"100%"} height={"100vh"}></Skeleton>
      )}

      {/* <div className="btn-panel-toggle" ref={toggleRef}>
        <Tooltip
          color="dark"
          position="bottom"
          label={approvalSettings.infoOpen ? "Hide Info Panel" : "Show Info Panel"}
        >
          <ActionIcon variant="default" onClick={() => setApprovalSettings((prevSettings) => ({ ...prevSettings, infoOpen: !prevSettings.infoOpen }))}>
            {approvalSettings.infoOpen ? (
              <IconLayoutSidebarRightCollapse
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            ) : (
              <IconLayoutSidebarRightExpand
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            )}
          </ActionIcon>
        </Tooltip>
      </div> */}
    </Box>
  );
};

export default StyleAssetViewer;
